import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import { useNotification } from '../../context/NotificationContext';
import { getAdminCategories, getAdminServices, bulkUploadServices, bulkAssignBrandToServices } from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { normalizeAdminListResponse } from '../../utils/adminListResponse';
import { useAdminListView } from '../../hooks/useAdminListView';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import CatalogItemActions from '../../components/CatalogItemActions';
import BrandBranchFilterFields from '../../components/BrandBranchFilterFields';
import { BRAND_FILTER_UNASSIGNED, brandFilterSelectValue } from '../../utils/contentScope';
import { createService, updateService, deleteService, duplicateService } from '../../services/serviceApi';
import { removeConfirmMessage, deactivateConfirmMessage } from '../../utils/portalCopy';
import AdminFormSection from '../../components/AdminFormSection';
import ExpandableText from '../../components/ExpandableText';
import {
  trimUrl,
  looksLikeHls,
  looksLikeVideoFile,
  googleDriveFileIdAny,
  driveEmbedPreviewUrl,
  driveThumbnailUrl,
  driveViewPageUrl,
  resolveMediaUrlForEmbed,
  resolveUrlForImageLoad,
  adminListThumbSrc,
  extractYouTubeId,
  youtubeEmbedSrcFromUrl,
} from '../../utils/adminMediaUrls';
import MediaUploadInput from '../../components/MediaUploadInput';
import { downloadServiceBulkUploadSample } from '../../utils/serviceBulkUploadTemplate';
import usePortalScope from '../../hooks/usePortalScope';
import { tenantListParams } from '../../utils/contentScope';
import './AdminPages.css';

/**
 * Direct file preview (MP4/WebM). Retries once with cache-bust for CDN/upload propagation;
 * mixed http→https is handled in resolveMediaUrlForEmbed.
 */
function ServiceDirectVideoPreview({ rawUrl, onGiveUp }) {
  const primary = useMemo(() => resolveMediaUrlForEmbed(trimUrl(rawUrl), 'video'), [rawUrl]);
  const [cacheBust, setCacheBust] = useState(null);

  useEffect(() => {
    setCacheBust(null);
  }, [rawUrl]);

  const src = useMemo(() => {
    if (!primary) return '';
    if (cacheBust == null) return primary;
    const sep = primary.includes('?') ? '&' : '?';
    return `${primary}${sep}_smPreview=${cacheBust}`;
  }, [primary, cacheBust]);

  if (!src) return null;

  return (
    <video
      key={src}
      className="admin-service-video-element"
      src={src}
      controls
      muted
      playsInline
      preload="metadata"
      referrerPolicy="no-referrer"
      onError={() => {
        if (cacheBust == null) setCacheBust(Date.now());
        else onGiveUp();
      }}
    />
  );
}

const ServiceManagement = () => {
  const { showError, showSuccess, confirm } = useNotification();
  const portal = usePortalScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const brands = portal.brands;
  const salons = portal.salons;
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    brandId: portal.lockBrandId || '',
    salonId: portal.lockSalonId || '',
    visibility: ''
  });
  const [formData, setFormData] = useState({
    visibility: portal.isBranchAdmin ? 'branch' : 'unassigned',
    brand: portal.lockBrandId || '',
    brandIds: portal.lockBrandId ? [portal.lockBrandId] : [],
    salonIds: portal.lockSalonId ? [portal.lockSalonId] : [],
    name: '',
    description: '',
    category: '',
    price: '',
    duration: '',
    image: '',
    videoUrl: '',
    showInRecommended: false,
    isActive: true
  });
  const [imagePreviewFailed, setImagePreviewFailed] = useState(false);
  const [videoPreviewFailed, setVideoPreviewFailed] = useState(false);
  const [bulkBrandId, setBulkBrandId] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const serviceFormPanelRef = useRef(null);

  useEffect(() => {
    if (!portal.lockBrandId && !portal.lockSalonId) return;
    setFilters((prev) => {
      const brandId = portal.lockBrandId || prev.brandId || '';
      const salonId = portal.lockSalonId || prev.salonId || '';
      if (prev.brandId === brandId && prev.salonId === salonId) return prev;
      return { ...prev, brandId, salonId };
    });
  }, [portal.lockBrandId, portal.lockSalonId]);

  const searchKey = useMemo(
    () => JSON.stringify({ searchTerm, ...filters }),
    [searchTerm, filters]
  );

  const getServiceListParams = useCallback(() => {
    const params = { ...tenantListParams(portal, filters) };
    if (searchTerm) params.search = searchTerm;
    if (!portal.isBranchAdmin && filters.visibility) {
      params.visibility = filters.visibility;
    }
    // Unassigned is not a brand id — never AND brand onto that filter.
    if (filters.visibility === 'unassigned' || filters.brandId === BRAND_FILTER_UNASSIGNED) {
      params.visibility = 'unassigned';
      delete params.brandId;
    }
    return params;
  }, [searchTerm, filters, portal]);

  const { items: services, loading, loadingMore, pagination, loadMore, reload } = useAdminInfiniteList({
    load: getAdminServices,
    resetKey: searchKey,
    getParams: getServiceListParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const listView = useAdminListView(services, (service) => service._id, {
    defaultView: 'grid',
    resetKey: searchKey
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    setEditingService(null);
    setFormData({
      visibility: portal.isBranchAdmin ? 'branch' : 'unassigned',
      brand: portal.lockBrandId || '',
      brandIds: portal.lockBrandId ? [portal.lockBrandId] : [],
      salonIds: portal.lockSalonId ? [portal.lockSalonId] : [],
      name: '',
      description: '',
      category: '',
      price: '',
      duration: '',
      image: '',
      videoUrl: '',
      showInRecommended: false,
      isActive: true
    });
    setShowForm(true);
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, portal.isBranchAdmin, portal.lockBrandId, portal.lockSalonId]);

  useEffect(() => {
    setImagePreviewFailed(false);
  }, [formData.image]);

  useEffect(() => {
    setVideoPreviewFailed(false);
  }, [formData.videoUrl]);

  /** Form mounts above the card grid; scroll it into view and focus first field (Edit / Add). */
  useEffect(() => {
    if (!showForm) return;
    const panel = serviceFormPanelRef.current;
    if (!panel) return;
    let focusTimer;
    const rafId = requestAnimationFrame(() => {
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      focusTimer = window.setTimeout(() => {
        const first = panel.querySelector(
          'form input:not([type="hidden"]):not([disabled]), form select:not([disabled]), form textarea:not([disabled])'
        );
        first?.focus({ preventScroll: true });
      }, 420);
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [showForm, editingService]);

  const fetchCategories = async () => {
    try {
      const response = await getAdminCategories({ all: true });
      const { data } = normalizeAdminListResponse(response);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const visibility = formData.visibility || 'unassigned';
      const payload = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        visibility,
        brand: formData.brand || undefined,
        brandIds: formData.brandIds || [],
        salonIds: formData.salonIds || [],
        price: parseFloat(formData.price),
        duration: parseInt(formData.duration),
        showInRecommended: Boolean(formData.showInRecommended),
        isActive: Boolean(formData.isActive),
        image: trimUrl(formData.image)
          ? resolveMediaUrlForEmbed(formData.image, 'image')
          : undefined,
        videoUrl: trimUrl(formData.videoUrl)
          ? resolveMediaUrlForEmbed(formData.videoUrl, 'video')
          : undefined
      };

      if (editingService) {
        await updateService(editingService._id, payload);
      } else {
        await createService(payload);
      }
      setShowForm(false);
      setEditingService(null);
      resetForm();
      showSuccess(editingService ? 'Service updated.' : 'Service created.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || `Failed to ${editingService ? 'update' : 'create'} service`);
    }
  };

  const resetForm = () => {
    setFormData({
      visibility: portal.isBranchAdmin ? 'branch' : 'unassigned',
      brand: portal.lockBrandId || '',
      brandIds: portal.lockBrandId ? [portal.lockBrandId] : [],
      salonIds: portal.lockSalonId ? [portal.lockSalonId] : [],
      name: '',
      description: '',
      category: '',
      price: '',
      duration: '',
      image: '',
      videoUrl: '',
      showInRecommended: false,
      isActive: true
    });
  };

  const handleEdit = (service) => {
    setEditingService(service);
    const brandId =
      service?.brand && typeof service.brand === 'object'
        ? service.brand._id
        : service?.brand || '';
    const brandIds = Array.isArray(service.brands) && service.brands.length
      ? service.brands.map((b) => (typeof b === 'object' ? b._id : b)).filter(Boolean)
      : brandId
        ? [brandId]
        : [];
    const salonIds = Array.isArray(service.salons)
      ? service.salons.map((s) => (typeof s === 'object' ? s._id : s)).filter(Boolean)
      : [];
    const categoryId =
      service?.category && typeof service.category === 'object'
        ? service.category._id
        : service?.category || '';
    const visibility = service.visibility
      || (salonIds.length ? 'branch' : brandId ? 'brand' : 'unassigned');
    setFormData({
      visibility,
      brand: brandId || portal.lockBrandId || '',
      brandIds: brandIds.length ? brandIds : (portal.lockBrandId ? [portal.lockBrandId] : []),
      salonIds,
      name: service.name || '',
      description: service.description || '',
      category: categoryId,
      price: service.price?.toString() || '',
      duration: service.duration?.toString() || '',
      image: service.image || '',
      videoUrl: service.videoUrl || '',
      showInRecommended: Boolean(service.showInRecommended),
      isActive: service.isActive !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Remove service',
      message: removeConfirmMessage('service'),
      confirmText: 'Remove',
      danger: true
    });
    if (!ok) return;
    try {
      await deleteService(id);
      showSuccess('Service removed.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to remove service');
    }
  };

  const handleToggleActive = async (service) => {
    const nextActive = service.isActive === false;
    if (!nextActive) {
      const ok = await confirm({
        title: 'Deactivate service',
        message: deactivateConfirmMessage('service'),
        confirmText: 'Deactivate',
        danger: true
      });
      if (!ok) return;
    }
    try {
      await updateService(service._id, { isActive: nextActive });
      showSuccess(nextActive ? 'Service activated.' : 'Service deactivated.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update service');
    }
  };

  const handleDuplicate = async (service) => {
    try {
      await duplicateService(service._id);
      showSuccess('Service duplicated into your catalog.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to duplicate service');
    }
  };

  const catalogActions = (service) => (
    <CatalogItemActions
      portal={portal}
      item={service}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onDuplicate={handleDuplicate}
      onToggle={handleToggleActive}
    />
  );

  const handleCancel = () => {
    setShowForm(false);
    setEditingService(null);
    resetForm();
  };

  const handleDownloadBulkSample = () => {
    const categoryNames = categories
      .filter((c) => c.isActive !== false)
      .map((c) => c.name)
      .filter(Boolean);
    const brandNames = brands
      .filter((b) => b.isActive !== false)
      .map((b) => b.name)
      .filter(Boolean);
    downloadServiceBulkUploadSample(categoryNames, brandNames);
  };

  const handleBulkAssignBrand = async () => {
    if (!bulkBrandId || listView.selectedCount === 0) return;

    try {
      setBulkAssigning(true);
      const serviceIds = Array.from(listView.selectedIds);
      const res = await bulkAssignBrandToServices({ serviceIds, brandId: bulkBrandId });
      showSuccess(res?.data?.message || 'Brand assigned to selected services.');
      listView.clearSelection();
      setBulkBrandId('');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to assign brand to services');
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await bulkUploadServices(formData);
      await reload();
      showSuccess(res?.data?.message || 'Services uploaded successfully.');
    } catch (error) {
      const data = error.response?.data;
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        // Show a compact message; full list can be large.
        const first = data.errors[0];
        const more = data.errors.length > 1 ? ` (+${data.errors.length - 1} more)` : '';
        showError(`${data.message || 'Upload failed due to validation errors.'} ${first}${more}`);
      } else {
        showError(data?.message || 'Failed to upload services');
      }
    }
    // Allow uploading the same file again if needed
    e.target.value = '';
  };

  const handleFilterChange = (key, value) => {
    if (key === 'brandId') {
      if (value === BRAND_FILTER_UNASSIGNED) {
        setFilters((prev) => ({
          ...prev,
          brandId: portal.lockBrandId || '',
          salonId: '',
          visibility: 'unassigned'
        }));
        return;
      }
      setFilters((prev) => ({
        ...prev,
        brandId: value,
        salonId: '',
        visibility: ''
      }));
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      brandId: portal.lockBrandId || '',
      salonId: portal.lockSalonId || '',
      visibility: ''
    });
  };

  const serviceColumns = useMemo(
    () => [
      { key: 'name', label: 'Name', render: (service) => <strong>{service.name}</strong> },
      {
        key: 'brand',
        label: 'Visibility',
        render: (service) => {
          const v = service.visibility || (service.brand ? 'brand' : 'unassigned');
          if (v === 'unassigned') return <span className="status-badge inactive">Unassigned</span>;
          if (v === 'all') return 'All brands & branches';
          if (v === 'branch') {
            const n = Array.isArray(service.salons) ? service.salons.length : 0;
            return n ? `${n} branch(es)` : 'Branch-specific';
          }
          const names = Array.isArray(service.brands) && service.brands.length
            ? service.brands.map((b) => b.name || b).join(', ')
            : (typeof service.brand === 'object' ? service.brand?.name : null);
          return names || 'Brand-wide';
        }
      },
      {
        key: 'category',
        label: 'Category',
        render: (service) =>
          typeof service.category === 'object' && service.category?.name
            ? service.category.name
            : service.category || 'N/A'
      },
      { key: 'price', label: 'Price', render: (service) => `₹${service.price}` },
      { key: 'duration', label: 'Duration', render: (service) => `${service.duration} min` },
      {
        key: 'status',
        label: 'Status',
        render: (service) => (
          <span className={`status-badge ${service.isActive !== false ? 'active' : 'inactive'}`}>
            {service.isActive !== false ? 'Active' : 'Inactive'}
          </span>
        )
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (service) => catalogActions(service)
      }
    ],
    [portal]
  );

  const serviceListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && services.length > 0 && (
        <p className="admin-infinite-end">All services loaded ({pagination.total} total).</p>
      )}
    </>
  );

  const serviceEmptyState = (
    <div className="empty-state">
      <p>No services found. Create your first service!</p>
    </div>
  );

  const renderServiceGridCard = (service) => {
    const videoDriveId =
      service.videoUrl && !looksLikeHls(service.videoUrl)
        ? googleDriveFileIdAny(service.videoUrl)
        : null;
    const imagePosterSrc =
      service.image && !looksLikeVideoFile(service.image)
        ? adminListThumbSrc(service.image)
        : undefined;

    return (
      <div className="admin-card admin-card--stretch">
        {(service.image || service.videoUrl) && (
          <div className="admin-card-media-strip">
            {service.image && !looksLikeVideoFile(service.image) && (
              <div className="admin-card-media-thumb-wrap">
                <img
                  src={imagePosterSrc}
                  alt=""
                  className="admin-card-media-thumb"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            {service.videoUrl && (
              <div className="admin-card-media-video-preview">
                {looksLikeHls(service.videoUrl) ? (
                  <span className="admin-card-media-badge">Video (HLS)</span>
                ) : extractYouTubeId(service.videoUrl) ? (
                  <iframe
                    title={`YouTube: ${service.name || 'service'}`}
                    className="admin-card-media-drive-iframe"
                    src={youtubeEmbedSrcFromUrl(service.videoUrl, {
                      controls: true,
                      autoplay: false,
                      mute: true
                    })}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  />
                ) : videoDriveId ? (
                  <iframe
                    title={`Video: ${service.name || 'service'}`}
                    className="admin-card-media-drive-iframe"
                    src={driveEmbedPreviewUrl(videoDriveId)}
                    allow="fullscreen"
                  />
                ) : (
                  <video
                    key={`${service._id}-${service.videoUrl || ''}`}
                    className="admin-card-media-video"
                    src={resolveMediaUrlForEmbed(service.videoUrl, 'video')}
                    poster={imagePosterSrc}
                    controls
                    muted
                    playsInline
                    preload="metadata"
                  />
                )}
              </div>
            )}
          </div>
        )}
        <div className="card-header">
          <div>
            <h3>{service.name}</h3>
            {service.description && (
              <p className="card-description">
                <ExpandableText text={service.description} lines={3} />
              </p>
            )}
          </div>
          <div className="card-actions">
            <span className={`status-badge ${service.isActive !== false ? 'active' : 'inactive'}`}>
              {service.isActive !== false ? 'Active' : 'Inactive'}
            </span>
            {catalogActions(service)}
          </div>
        </div>
        <div className="card-details">
          {(() => {
            const v = service.visibility || (service.brand ? 'brand' : 'unassigned');
            if (v === 'unassigned') {
              return <p><strong>Visibility:</strong> <span className="status-badge inactive">Unassigned</span></p>;
            }
            if (v === 'all') return <p><strong>Visibility:</strong> All brands & branches</p>;
            if (typeof service.brand === 'object' && service.brand?.name) {
              return <p><strong>Brand:</strong> {service.brand.name}</p>;
            }
            return null;
          })()}
          <p>
            <strong>Category:</strong>{' '}
            {typeof service.category === 'object' && service.category?.name
              ? service.category.name
              : service.category || 'N/A'}
          </p>
          <p><strong>Price:</strong> ₹{service.price}</p>
          <p><strong>Duration:</strong> {service.duration} minutes</p>
          <p>
            <strong>Recommended row:</strong>{' '}
            {service.showInRecommended ? (
              <span className="status-badge active">Yes</span>
            ) : (
              <span className="status-badge inactive">No</span>
            )}
          </p>
          {service.image && (
            <p>
              <strong>Image:</strong>{' '}
              <ExpandableText text={service.image} lines={1} minCharsForToggle={60} />
            </p>
          )}
          {service.videoUrl && (
            <p>
              <strong>Video:</strong>{' '}
              <ExpandableText text={service.videoUrl} lines={1} minCharsForToggle={60} />
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <AdminLayout
      pageTitle="Services & Pricing"
      subtitle={
        pagination.total
          ? `${services.length} of ${pagination.total} services · scroll for more`
          : undefined
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header admin-page-header--split">
          <div className="admin-page-header-title-row">
            <h1 style={{ margin: 0 }}>Service List</h1>
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search services by name, category, brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="admin-page-header-actions">
            {portal.isSuperAdmin && (
              <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleDownloadBulkSample}
            >
              Download Sample CSV
            </button>
            <label className="btn btn-secondary admin-header-file-btn">
              Upload CSV/Excel
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleBulkUpload}
                style={{ display: 'none' }}
              />
            </label>
              </>
            )}
            <button 
              type="button"
              className="btn btn-primary" 
              onClick={() => {
                setEditingService(null);
                resetForm();
                setShowForm(true);
              }}
            >
              + Add Service
            </button>
          </div>
        </div>

        {portal.isSuperAdmin && (
        <p className="form-hint" style={{ marginTop: 0, marginBottom: '16px' }}>
          Bulk upload format: required columns <strong>name</strong>, <strong>category</strong>,{' '}
          <strong>price</strong>, <strong>duration</strong> (minutes). Optional:{' '}
          <strong>brand</strong> (name, brand code, or ID), <strong>description</strong>,{' '}
          <strong>image</strong>, <strong>videoUrl</strong>.
          Download the sample CSV, edit the dummy rows, then upload as CSV or Excel.
          Category and brand must match existing names (or IDs). Rows without a brand can be
          assigned later using multi-select.
        </p>
        )}

        {!portal.isBranchAdmin && (
        <div className="admin-card filter-card">
          <h3>Filters</h3>
          <div className="filter-grid">
            <BrandBranchFilterFields
              portal={portal}
              filters={filters}
              onChange={handleFilterChange}
              brands={brands}
              salons={salons}
              includeUnassignedOption
              brandSelectValue={brandFilterSelectValue(filters, portal.lockBrandId)}
            />
            <div className="form-group filter-actions">
              <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>
        )}

      {loading ? (
        <Loader variant="dark" fullScreen={false} />
      ) : (
        <>
      {showForm && (
        <div
          ref={serviceFormPanelRef}
          className="admin-card admin-service-form-panel"
          style={{ marginBottom: '24px' }}
        >
          <h2 style={{ fontFamily: "'Lato', sans-serif", fontWeight: 700, fontSize: '16px', color: '#FFFFFF', marginBottom: '20px' }}>
            {editingService ? 'Edit Service' : 'Add Service'}
          </h2>
          <form onSubmit={handleSubmit}>
            <AdminFormSection title="Visibility" defaultOpen>
            <div className="form-group">
              <label>Visibility *</label>
              <select
                value={formData.visibility}
                disabled={portal.isBranchAdmin}
                onChange={(e) => {
                  const visibility = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    visibility,
                    brandIds: visibility === 'all' ? [] : (portal.lockBrandId ? [portal.lockBrandId] : prev.brandIds),
                    brand: visibility === 'all' ? '' : (portal.lockBrandId || prev.brand),
                    salonIds: visibility === 'branch'
                      ? (portal.lockSalonId ? [portal.lockSalonId] : prev.salonIds)
                      : []
                  }));
                }}
                required
              >
                {!portal.isBranchAdmin && (
                  <option value="unassigned">Unassigned (not visible anywhere)</option>
                )}
                {portal.isSuperAdmin && (
                  <option value="all">All brands & branches</option>
                )}
                {!portal.isBranchAdmin && (
                  <option value="brand">
                    {portal.isBrandAdmin ? 'All branches in this brand' : 'Specific brand(s)'}
                  </option>
                )}
                <option value="branch">Specific branch(es)</option>
              </select>
              <p className="form-hint">
                Unassigned services stay hidden from brands and branches until you assign them.
              </p>
            </div>

            {formData.visibility === 'brand' && !portal.lockBrandId && (
              <div className="form-group">
                <label>Brand(s) *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 160, overflowY: 'auto' }}>
                  {brands.map((brand) => {
                    const checked = (formData.brandIds || []).map(String).includes(String(brand._id));
                    return (
                      <label key={brand._id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const sid = String(brand._id);
                            const set = new Set((formData.brandIds || []).map(String));
                            if (set.has(sid)) set.delete(sid);
                            else set.add(sid);
                            const brandIds = [...set];
                            setFormData((prev) => ({
                              ...prev,
                              brandIds,
                              brand: brandIds[0] || ''
                            }));
                          }}
                        />
                        <span>{brand.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {formData.visibility === 'brand' && portal.lockBrandId && (
              <p className="form-hint">Will be available to all branches under your brand.</p>
            )}

            {formData.visibility === 'branch' && (
              <div className="form-group">
                <label>Branch(es) *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 160, overflowY: 'auto' }}>
                  {salons
                    .filter((s) => {
                      if (portal.lockSalonId) return String(s._id) === String(portal.lockSalonId);
                      if (portal.lockBrandId) {
                        return String(s.brand?._id || s.brand) === String(portal.lockBrandId);
                      }
                      if (formData.brandIds?.length) {
                        return formData.brandIds.map(String).includes(String(s.brand?._id || s.brand));
                      }
                      return true;
                    })
                    .map((salon) => {
                      const checked = (formData.salonIds || []).map(String).includes(String(salon._id))
                        || Boolean(portal.lockSalonId);
                      return (
                        <label key={salon._id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={Boolean(portal.lockSalonId)}
                            onChange={() => {
                              if (portal.lockSalonId) return;
                              const sid = String(salon._id);
                              const set = new Set((formData.salonIds || []).map(String));
                              if (set.has(sid)) set.delete(sid);
                              else set.add(sid);
                              setFormData((prev) => ({ ...prev, salonIds: [...set] }));
                            }}
                          />
                          <span>
                            {salon.brand?.name ? `${salon.brand.name} – ` : ''}
                            {salon.name || salon.branch}
                          </span>
                        </label>
                      );
                    })}
                </div>
              </div>
            )}

            </AdminFormSection>

            <AdminFormSection title="Details" defaultOpen>
            <div className="form-group">
              <label>Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                <option value="">Select a category</option>
                {categories
                  .filter(cat => cat.isActive !== false)
                  .map(category => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
              </select>
              {categories.length === 0 && (
                <p style={{ color: '#ff6b6b', fontSize: '12px', marginTop: '5px' }}>
                  No categories available. Please create a category first.
                </p>
              )}
            </div>
            <div className="form-group">
              <label>Service Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Price (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Duration (minutes) *</label>
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                id="isActive"
                checked={Boolean(formData.isActive)}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <label htmlFor="isActive" style={{ margin: 0, fontWeight: 600 }}>
                Service is active (visible in catalog)
              </label>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                id="showInRecommended"
                checked={Boolean(formData.showInRecommended)}
                onChange={(e) => setFormData({ ...formData, showInRecommended: e.target.checked })}
              />
              <label htmlFor="showInRecommended" style={{ margin: 0, fontWeight: 600 }}>
                Show in customer dashboard &quot;Recommended&quot; row (TV + mobile)
              </label>
            </div>
            </AdminFormSection>

            <AdminFormSection title="Media" defaultOpen={false}>
            <MediaUploadInput
              label="Image URL (thumbnail)"
              value={formData.image}
              onChange={(v) => setFormData({ ...formData, image: v })}
              accept="image/*"
              maxSizeMb={2}
              showInlinePreview={false}
              placeholder="https://… (poster / still for lists and TV cards)"
              helperText="Paste URL or drag & drop image file. Recommended: <= 2MB."
            />
            <MediaUploadInput
              label="Video URL (preview)"
              value={formData.videoUrl}
              onChange={(v) => setFormData({ ...formData, videoUrl: v })}
              accept="video/*"
              maxSizeMb={20}
              showInlinePreview={false}
              placeholder="https://… (MP4, HLS, or YouTube — optional; TV plays when card is focused)"
              helperText="Paste URL or drag & drop video file. YouTube links use embed playback (not a direct file). Recommended: <= 20MB for uploads."
            />
            {(trimUrl(formData.image) || trimUrl(formData.videoUrl)) && (
              <div className="admin-service-media-preview-section">
                <div className="admin-service-media-preview-title">Preview</div>
                <div className="admin-service-media-preview-row">
                  {trimUrl(formData.image) && (
                    <div className="admin-service-media-preview-block">
                      <span className="admin-service-media-label">Thumbnail (image URL)</span>
                      <div className="admin-service-media-frame admin-service-media-frame--image">
                        {looksLikeVideoFile(trimUrl(formData.image)) ? (
                          <div className="admin-service-media-hint">
                            This URL looks like a video file. Use a poster image here, and put the video in{' '}
                            <strong>Video URL</strong> for the best experience.
                          </div>
                        ) : (() => {
                          const driveId = googleDriveFileIdAny(trimUrl(formData.image));
                          if (driveId) {
                            return (
                              <div className="admin-drive-preview-wrap">
                                <img
                                  className="admin-drive-thumb-inline"
                                  src={driveThumbnailUrl(driveId)}
                                  alt=""
                                  referrerPolicy="no-referrer"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                                <iframe
                                  title="Google Drive preview"
                                  src={driveEmbedPreviewUrl(driveId)}
                                  className="admin-drive-preview-iframe"
                                  allow="fullscreen"
                                />
                                <a
                                  href={driveViewPageUrl(driveId)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="admin-service-media-link"
                                >
                                  Open in Google Drive
                                </a>
                              </div>
                            );
                          }
                          if (imagePreviewFailed) {
                            const attempted = resolveUrlForImageLoad(formData.image);
                            const isMixedContent =
                              typeof window !== 'undefined' &&
                              window.location?.protocol === 'https:' &&
                              /^http:\/\//i.test(String(attempted || ''));
                            return (
                              <div className="admin-service-media-hint">
                                Could not load image.
                                <div style={{ marginTop: 6, fontSize: 12, opacity: 0.9 }}>
                                  Preview URL:{' '}
                                  <code style={{ wordBreak: 'break-all' }}>
                                    {attempted || '(empty)'}
                                  </code>
                                </div>
                                {isMixedContent ? (
                                  <div style={{ marginTop: 6 }}>
                                    This looks like mixed content: <code>http://</code> media on an <code>https://</code>{' '}
                                    admin page is blocked by the browser. Use <code>https://</code>.
                                  </div>
                                ) : (
                                  <div style={{ marginTop: 6 }}>
                                    If the URL is <code>https://</code> but still fails, it’s usually a{' '}
                                    <strong>403/permission</strong> issue (file not public) or the object doesn’t exist
                                    yet. Try opening the URL in a new tab to confirm it loads.
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return (
                            <img
                              key={resolveUrlForImageLoad(formData.image)}
                              src={resolveUrlForImageLoad(formData.image)}
                              alt="Service thumbnail preview"
                              referrerPolicy="no-referrer"
                              onError={() => setImagePreviewFailed(true)}
                            />
                          );
                        })()}
                      </div>
                    </div>
                  )}
                  {trimUrl(formData.videoUrl) && (
                    <div className="admin-service-media-preview-block">
                      <span className="admin-service-media-label">Video preview</span>
                      <div className="admin-service-media-frame admin-service-media-frame--video">
                        {looksLikeHls(trimUrl(formData.videoUrl)) ? (
                          <div className="admin-service-media-hint-wrap">
                            <div className="admin-service-media-hint">
                              HLS (.m3u8) often cannot play inline in this browser. It will still work on Android TV
                              (ExoPlayer). Open the link to verify in another tab:
                            </div>
                            <a
                              href={resolveMediaUrlForEmbed(formData.videoUrl, 'video')}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="admin-service-media-link"
                            >
                              Open stream URL
                            </a>
                          </div>
                        ) : (() => {
                          const vDriveId = googleDriveFileIdAny(trimUrl(formData.videoUrl));
                          if (vDriveId) {
                            return (
                              <div className="admin-drive-preview-wrap admin-drive-preview-wrap--video">
                                <iframe
                                  title="Google Drive video preview"
                                  src={driveEmbedPreviewUrl(vDriveId)}
                                  className="admin-drive-preview-iframe"
                                  allow="fullscreen"
                                />
                                <a
                                  href={driveViewPageUrl(vDriveId)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="admin-service-media-link"
                                >
                                  Open in Google Drive
                                </a>
                              </div>
                            );
                          }
                          if (extractYouTubeId(trimUrl(formData.videoUrl))) {
                            return (
                              <iframe
                                title="YouTube preview"
                                src={youtubeEmbedSrcFromUrl(formData.videoUrl, {
                                  controls: true,
                                  autoplay: false,
                                  mute: true,
                                })}
                                className="admin-drive-preview-iframe"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                              />
                            );
                          }
                          if (videoPreviewFailed) {
                            return (
                              <div className="admin-service-media-hint">
                                Preview could not load in this browser (file may still be finishing upload, URL typo,
                                blocked mixed http/https on an https admin site, or CDN hotlink rules). The link is often
                                still valid on TV — open it below to verify.
                                <a
                                  href={resolveMediaUrlForEmbed(formData.videoUrl, 'video')}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="admin-service-media-link"
                                  style={{ display: 'block', marginTop: 8 }}
                                >
                                  Open video URL
                                </a>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  style={{ marginTop: 10 }}
                                  onClick={() => setVideoPreviewFailed(false)}
                                >
                                  Retry preview
                                </button>
                              </div>
                            );
                          }
                          return (
                            <ServiceDirectVideoPreview
                              rawUrl={formData.videoUrl}
                              onGiveUp={() => setVideoPreviewFailed(true)}
                            />
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            </AdminFormSection>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingService ? 'Update Service' : 'Create Service'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <AdminListViewControls
        viewMode={listView.viewMode}
        onViewModeChange={listView.setViewMode}
        selectedCount={listView.selectedCount}
        onSelectAll={listView.selectAll}
        onClearSelection={listView.clearSelection}
        allSelected={listView.allSelected}
        someSelected={listView.someSelected}
        totalCount={services.length}
        resultTotal={pagination.total}
        showSelection={portal.isSuperAdmin}
        selectionHint="Select services to bulk-assign a brand"
      />
      {portal.isSuperAdmin && listView.selectedCount === 0 && services.length > 0 && (
        <p className="admin-bulk-hint">
          Tip: select one or more services (checkboxes), then assign a brand to all of them at once.
        </p>
      )}
      {portal.isSuperAdmin && listView.selectedCount > 0 && (
        <div className="admin-bulk-actions-bar">
          <span className="admin-bulk-actions-label">
            Assign brand to {listView.selectedCount} selected service
            {listView.selectedCount === 1 ? '' : 's'}
          </span>
          <select
            value={bulkBrandId}
            onChange={(e) => setBulkBrandId(e.target.value)}
            aria-label="Brand for bulk assignment"
          >
            <option value="">Select brand…</option>
            {brands.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!bulkBrandId || bulkAssigning}
            onClick={handleBulkAssignBrand}
          >
            {bulkAssigning ? 'Assigning…' : 'Assign brand'}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              listView.clearSelection();
              setBulkBrandId('');
            }}
          >
            Clear selection
          </button>
        </div>
      )}
      <AdminSelectableList
        items={services}
        getItemId={(service) => service._id}
        viewMode={listView.viewMode}
        selectable={portal.isSuperAdmin}
        isSelected={listView.isSelected}
        onToggleItem={listView.toggleItem}
        allSelected={listView.allSelected}
        someSelected={listView.someSelected}
        onSelectAll={listView.selectAll}
        onClearSelection={listView.clearSelection}
        columns={serviceColumns}
        renderGridCard={renderServiceGridCard}
        emptyState={serviceEmptyState}
        scrollRef={scrollRef}
        footer={serviceListFooter}
      />
      </>
      )}
      </div>
    </AdminLayout>
  );
};

export default ServiceManagement;
